import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Button } from '../../../../shared/components/Button.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { maskedAccount } from '../../../../shared/contracts/bank-account.js';
import { M15_HOLD_REASONS, M15_PAYMENT_MODES } from '../../../../shared/contracts/backend-enums.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** An approved voucher awaiting payment (GET /v1/payments/payment-entry). */
interface PayableVoucher {
  readonly proposalId: string;
  readonly eligibilityId: string;
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly proposalAmount: number;
  readonly tdsAmount: number;
  readonly otherDeductions: number;
  readonly netAmount: number;
  readonly beneficiaryName: string;
  readonly beneficiaryAccountNo: string | null;
  readonly beneficiaryIfsc: string | null;
}

interface Payable {
  readonly bankDetailsRequired: boolean;
  readonly vouchers: readonly PayableVoucher[];
}

interface Entered {
  readonly advice: { readonly adviceNumber: string };
}

const cell = { padding: '12px 14px', borderBottom: '1px solid var(--color-border-default)', verticalAlign: 'middle' } as const;
const head = { ...cell, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-muted)', textAlign: 'left', background: 'var(--color-bg-muted)' } as const;
const control = { height: 40, padding: '0 12px', borderRadius: 8, border: '1px solid var(--color-border-default)', fontSize: 14, background: 'var(--color-bg-surface)' } as const;
const label = { display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, fontSize: 14 } as const;

/**
 * L15.15 — Payment Entry (owner, 30 Oct: "Sales Accounts enters the payment").
 * The approved vouchers awaiting payment are listed; Sales Accounts picks one
 * and enters the day it was paid, the mode and the bank's reference. Whether
 * the bank details are required is the tenant's setting; the beneficiary is
 * the one the voucher took from the vendor master and is never typed. Entering
 * pays the bill and issues the vendor's payment advice. A voucher may instead
 * be held (owner, 3 Nov), for a listed reason, until it is released on
 * Payment Holds.
 */
export function PaymentEntryPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [data, setData] = useState<Payable | null>(null);
  const [selected, setSelected] = useState<PayableVoucher | null>(null);
  const [holding, setHolding] = useState<PayableVoucher | null>(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [mode, setMode] = useState<string>(M15_PAYMENT_MODES[0]);
  const [utrNumber, setUtrNumber] = useState('');
  const [bankReferenceNo, setBankReferenceNo] = useState('');
  const [holdReason, setHoldReason] = useState<string>(M15_HOLD_REASONS[0]);
  const [holdDetail, setHoldDetail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiRequest<Payable>('/v1/payments/payment-entry', { token })
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const pick = (v: PayableVoucher) => {
    setSelected(v);
    setHolding(null);
    setMode(M15_PAYMENT_MODES[0]);
    setPaymentDate(''); setUtrNumber(''); setBankReferenceNo('');
    setError(null); setNotice(null);
  };

  const pickHold = (v: PayableVoucher) => {
    setHolding(v);
    setSelected(null);
    setHoldReason(M15_HOLD_REASONS[0]);
    setHoldDetail('');
    setError(null); setNotice(null);
  };

  const bankRequired = data?.bankDetailsRequired ?? true;
  const blocker = !paymentDate ? t('finance.entry.needDate')
    : bankRequired && !utrNumber.trim() && !bankReferenceNo.trim() ? t('finance.entry.needReference')
    : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || blocker) return;
    setBusy(true); setError(null);
    try {
      const r = await apiRequest<Entered>(`/v1/payments/payment-entry/${selected.proposalId}`, {
        token, method: 'POST',
        body: {
          paymentDate, paymentModeCode: mode,
          utrNumber: utrNumber.trim() || undefined,
          bankReferenceNo: bankReferenceNo.trim() || undefined,
        },
      });
      setNotice(t('finance.entry.entered', { advice: r.advice.adviceNumber }));
      setSelected(null);
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const submitHold = async (event: FormEvent) => {
    event.preventDefault();
    if (!holding || !holdDetail.trim()) return;
    setBusy(true); setError(null);
    try {
      await apiRequest<unknown>('/v1/payments/hold', {
        token, method: 'POST',
        body: { eligibilityId: holding.eligibilityId, holdReasonCode: holdReason, holdReasonDetail: holdDetail.trim() },
      });
      setNotice(t('finance.entry.held'));
      setHolding(null);
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="tl-page" style={{ padding: 24 }}>
      <PageHeader title={t('finance.entry.title')} subtitle={t('finance.entry.subtitle')} />
      <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
        {bankRequired ? t('finance.entry.bankRequired') : t('finance.entry.bankOptional')}
      </p>
      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <div role="status"><Alert tone="success">{notice}</Alert></div>}
      {!data && !error && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.entry.loading')}</p>}
      {data && data.vouchers.length === 0 && <div data-testid="entry-empty"><EmptyState title={t('finance.entry.empty')} /></div>}
      {data && data.vouchers.length > 0 && (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="approved-vouchers" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={head}>{t('finance.entry.colBill')}</th>
                  <th style={head}>{t('finance.entry.colVendor')}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{t('finance.entry.colGross')}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{t('finance.entry.colTds')}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{t('finance.entry.colNet')}</th>
                  <th style={head}>{t('finance.entry.colBank')}</th>
                  <th style={head}>{t('finance.entry.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {data.vouchers.map((v) => (
                  <tr key={v.proposalId} style={selected?.proposalId === v.proposalId || holding?.proposalId === v.proposalId ? { background: 'var(--color-bg-selected)' } : undefined}>
                    <td style={cell}>{billRefLabel(v.bill, day)}</td>
                    <td style={cell}>{personLabel(v.vendorName, v.vendorCode) ?? '—'}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(v.proposalAmount)}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{money(v.tdsAmount)}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }} data-testid={`net-${v.proposalId}`}>{money(v.netAmount)}</td>
                    <td style={cell} data-testid={`beneficiary-${v.proposalId}`}>
                      {v.beneficiaryName}
                      {v.beneficiaryAccountNo ? ` · ${maskedAccount(v.beneficiaryAccountNo)} · ${v.beneficiaryIfsc ?? ''}` : ''}
                    </td>
                    <td style={cell}>
                      <div className="tl-stack tl-stack--row" style={{ gap: 8 }}>
                        <Button size="sm" variant="primary" data-testid={`pay-${v.proposalId}`} onClick={() => pick(v)}>{t('finance.entry.enter')}</Button>
                        <Button size="sm" variant="secondary" data-testid={`hold-${v.proposalId}`} onClick={() => pickHold(v)}>{t('finance.entry.hold')}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selected && (
        <Card title={t('finance.entry.paying', { vendor: selected.beneficiaryName, amount: `₹${money(selected.netAmount)}` })}>
          <form aria-label="payment-entry" onSubmit={(e) => void submit(e)}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <label style={label}>{t('finance.entry.paymentDate')}
              <DateInput aria-label="payment-date" type="date" value={paymentDate} onValueChange={setPaymentDate} required />
            </label>
            <label style={label}>{t('finance.entry.mode')}
              <select aria-label="payment-mode" style={control} value={mode} onChange={(e) => setMode(e.target.value)}>
                {M15_PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label style={label}>{t('finance.entry.utr')}
              <input aria-label="utr" style={control} value={utrNumber} maxLength={30} onChange={(e) => setUtrNumber(e.target.value)} />
            </label>
            <label style={label}>{t('finance.entry.bankReference')}
              <input aria-label="bank-reference" style={control} value={bankReferenceNo} maxLength={50} onChange={(e) => setBankReferenceNo(e.target.value)} />
            </label>
            <div style={{ gridColumn: '1 / -1' }}>
              {blocker && <p role="status" aria-label="entry-blocked-reason" style={{ color: 'var(--color-text-muted)', margin: '0 0 12px' }}>{blocker}</p>}
              <div className="tl-stack tl-stack--row" style={{ gap: 8 }}>
                <Button type="submit" variant="primary" disabled={busy || !!blocker} loading={busy}>{busy ? t('finance.entry.saving') : t('finance.entry.save')}</Button>
                <Button type="button" variant="ghost" onClick={() => setSelected(null)}>{t('finance.entry.cancel')}</Button>
              </div>
            </div>
          </form>
        </Card>
      )}

      {holding && (
        <Card title={t('finance.entry.holding', { vendor: holding.beneficiaryName, amount: `₹${money(holding.netAmount)}` })}>
          <form aria-label="payment-hold" onSubmit={(e) => void submitHold(e)} style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
            <label style={label}>{t('finance.entry.holdReason')}
              <select aria-label="hold-reason" style={control} value={holdReason} onChange={(e) => setHoldReason(e.target.value)}>
                {M15_HOLD_REASONS.map((r) => <option key={r} value={r}>{t(`finance.holdReason.${r}`)}</option>)}
              </select>
            </label>
            <label style={label}>{t('finance.entry.holdDetail')}
              <textarea aria-label="hold-detail" rows={3} maxLength={500} value={holdDetail} onChange={(e) => setHoldDetail(e.target.value)}
                style={{ ...control, height: 'auto', padding: 12, fontFamily: 'inherit', resize: 'vertical' }} />
            </label>
            {!holdDetail.trim() && <p role="status" aria-label="hold-blocked-reason" style={{ color: 'var(--color-text-muted)', margin: 0 }}>{t('finance.entry.needHoldDetail')}</p>}
            <div className="tl-stack tl-stack--row" style={{ gap: 8 }}>
              <Button type="submit" variant="danger" disabled={busy || !holdDetail.trim()} loading={busy}>{t('finance.entry.holdSave')}</Button>
              <Button type="button" variant="ghost" onClick={() => setHolding(null)}>{t('finance.entry.cancel')}</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
